import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Globe, Search, BarChart3, Link2, 
  Settings, LogOut, ChevronLeft, Menu, Plus, Zap, Clock, FileCheck,
  Rocket, Bell, TrendingUp, DollarSign, ClipboardCheck, Server, Mail,
  ShieldCheck
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { authAPI, clearTokens } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const navItems = [
  { path: '/', label: 'Gosterge Paneli', icon: LayoutDashboard, testId: 'sidebar-nav-dashboard-link' },
  { path: '/sites', label: 'Site Yoneticisi', icon: Globe, testId: 'sidebar-nav-site-manager-link' },
  { path: '/scheduler', label: 'Zamanlayici', icon: Clock, testId: 'sidebar-nav-scheduler-link' },
  { path: '/review', label: 'Inceleme Kuyrugu', icon: FileCheck, testId: 'sidebar-nav-review-link' },
  { path: '/seo', label: 'SEO Paneli', icon: Search, testId: 'sidebar-nav-seo-dashboard-link' },
  { path: '/analytics', label: 'Analitik', icon: TrendingUp, testId: 'sidebar-nav-analytics-link' },
  { path: '/revenue', label: 'Gelir', icon: DollarSign, testId: 'sidebar-nav-revenue-link' },
  { path: '/deploy', label: 'Deploy', icon: Rocket, testId: 'sidebar-nav-deploy-link' },
  { path: '/production-health', label: 'Produksiyon Saglik', icon: ShieldCheck, testId: 'sidebar-nav-production-health-link' },
  { path: '/monitoring', label: 'Izleme', icon: Bell, testId: 'sidebar-nav-monitoring-link' },
  { path: '/launch', label: 'Canli Kontrol', icon: ClipboardCheck, testId: 'sidebar-nav-launch-link' },
  { path: '/servers', label: 'Sunucular', icon: Server, testId: 'sidebar-nav-servers-link' },
  { path: '/email-ops', label: 'Email Ops', icon: Mail, testId: 'sidebar-nav-email-ops-link' },
  { path: '/v3-content', label: 'V3 Icerik', icon: Zap, testId: 'sidebar-nav-v3-content-link' },
  { path: '/sitemap-health', label: 'Sitemap Saglik', icon: Globe, testId: 'sidebar-nav-sitemap-health-link' },
];

function SidebarContent({ collapsed, onCollapse }) {
  const location = useLocation();

  return (
    <div className="flex flex-col h-full bg-[hsl(var(--sidebar-background))] border-r border-border/50">
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-border/30">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
          <Zap className="w-5 h-5" />
        </div>
        {!collapsed && (
          <div>
            <h1 className="font-semibold text-base tracking-tight font-['Space_Grotesk']">TrafficOS</h1>
            <p className="text-[10px] text-muted-foreground tracking-wider uppercase">Affiliate Network OS</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = item.path === '/' 
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={item.testId}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150',
                isActive
                  ? 'sidebar-active text-foreground font-medium'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/40'
              )}
            >
              <Icon className="w-[18px] h-[18px] shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="px-3 py-4 border-t border-border/30 space-y-1">
        <button
          onClick={() => {
            authAPI.logout().catch(() => {}).finally(() => {
              clearTokens();
              window.location.href = '/login';
            });
          }}
          data-testid="sidebar-nav-logout-button"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/40 w-full transition-colors duration-150"
        >
          <LogOut className="w-[18px] h-[18px]" />
          {!collapsed && <span>Cikis</span>}
        </button>
      </div>
    </div>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      {/* Desktop */}
      <aside className={cn(
        'hidden lg:flex flex-col shrink-0 transition-[width] duration-200',
        collapsed ? 'w-[72px]' : 'w-[260px]'
      )}>
        <SidebarContent collapsed={collapsed} onCollapse={setCollapsed} />
      </aside>

      {/* Mobile */}
      <Sheet>
        <SheetTrigger asChild className="lg:hidden">
          <Button variant="ghost" size="icon" className="fixed top-3 left-3 z-50">
            <Menu className="w-5 h-5" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-[260px] p-0 bg-[hsl(var(--sidebar-background))]">
          <SidebarContent collapsed={false} />
        </SheetContent>
      </Sheet>
    </>
  );
}
